import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X, ArrowUpRight, CalendarCheck, Check } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Flag, LANGS } from "@/components/common/Flags";
import { BookingBar, useAnchorPassed, useIsMobile } from "@/components/booking/BookingBar";
import { useLang } from "@/lib/i18n";

export const NAV = [
  { to: "/rom", nn: "Rom og hytter", en: "Rooms & cabins", id: "rooms" },
  { to: "/mat", nn: "Restaurant", en: "Restaurant", id: "dining" },
  { to: "/arrangement", nn: "Selskap og møte", en: "Events & meetings", id: "events" },
  { to: "/praktisk", nn: "Praktisk", en: "Practical", id: "practical" },
  { to: "/om-oss", nn: "Om hotellet", en: "About the hotel", id: "about" },
];

export const PHONE = { tel: "tel:+4753488000", display: "53 48 80 00", intl: "+47 53 48 80 00" };

export function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(() => (typeof window !== "undefined" ? window.scrollY > threshold : false));
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > threshold);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [threshold]);
  return scrolled;
}

/* ------------------------------------------------------------------ language */

/** Flag-only language chooser (no "NN"/"EN" text). `light` = sitting on top of a photo. */
export const LangMenu = ({ testid = "header-language-toggle", className = "", align = "end", light = false }) => {
  const { lang, setLang, tx } = useLang();
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-md transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            light ? "hover:bg-white/10 data-[state=open]:bg-white/10" : "hover:bg-foreground/[0.05] data-[state=open]:bg-foreground/[0.05]"
          } ${className}`}
          aria-label={`${current.label} – ${tx("byt språk", "change language")}`}
          data-testid={testid}
        >
          <Flag code={current.code} className="h-[20px] w-[20px]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} sideOffset={12} className="w-44 rounded-lg border-border/70 bg-card p-1 shadow-pop">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={() => setLang(l.code)}
            data-testid={`${testid}-${l.code}`}
            className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-[0.9375rem] focus:bg-secondary ${lang === l.code ? "font-medium text-foreground" : "text-foreground/75"}`}
          >
            <Flag code={l.code} className="h-[18px] w-[18px]" />
            <span className="flex-1">{l.label}</span>
            {lang === l.code && <Check className="h-4 w-4" strokeWidth={1.75} />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Segmented language switch with labels – footer and mobile menu only (header is flag-only). */
export const LangSwitch = ({ testid = "footer-language-toggle", light = false, className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center rounded-md border p-0.5 ${light ? "border-white/15" : "border-border"} ${className}`} role="group" aria-label="Language" data-testid={testid}>
      {LANGS.map((l) => {
        const active = lang === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            aria-pressed={active}
            data-testid={`${testid}-${l.code}`}
            className={`inline-flex h-8 items-center gap-2 rounded-[3px] pl-2 pr-3 text-[0.8125rem] font-medium transition-colors ${
              active
                ? light ? "bg-white text-foreground" : "bg-foreground text-background"
                : light ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Flag code={l.code} className="h-4 w-4" /> {l.label}
          </button>
        );
      })}
    </div>
  );
};

export const LangToggle = LangSwitch;

export const Logo = ({ className = "h-8 lg:h-9", light = false }) => (
  <img src="/images/logo.png" alt="Rosendal Fjordhotel" className={`${className} w-auto transition-[filter] duration-300 ${light ? "brightness-0 invert" : ""}`} />
);

/* ------------------------------------------------------------------ desktop nav */

/** Quiet text links with a hairline underline that grows in on hover and rests under the active page. */
const DesktopNav = ({ light }) => {
  const { tx } = useLang();
  const { pathname } = useLocation();
  const activeId = NAV.find((n) => pathname.startsWith(n.to))?.id || null;
  return (
    <nav className="flex items-center gap-7 xl:gap-9" aria-label={tx("Hovudmeny", "Main menu")}>
      {NAV.map((n) => {
        const isActive = activeId === n.id;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`nav-${n.id}`}
            data-active={isActive ? "true" : "false"}
            className={`link-grow py-1 text-[0.875rem] font-medium tracking-[0.01em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              light ? (isActive ? "text-white" : "text-white/80 hover:text-white") : isActive ? "text-foreground" : "text-foreground/70 hover:text-foreground"
            }`}
          >
            {tx(n.nn, n.en)}
          </NavLink>
        );
      })}
    </nav>
  );
};

/* ------------------------------------------------------------------ header */

/**
 * Full-width header. Transparent over a photo hero; solid linen with a hairline once you scroll
 * (or on plain pages). Never a shrinking pill. Three columns keep the nav centred.
 * On the pages with a hero search, the compact booking bar takes the nav's place after you scroll past it.
 */
export const Header = ({ showSearch = false, overlay = false }) => {
  const { tx } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const scrolled = useScrolled(40);
  const isMobile = useIsMobile();
  const passed = useAnchorPassed({ offset: 72 });
  const searchVisible = showSearch && passed && !isMobile;
  const light = overlay && !scrolled;
  useEffect(() => setOpen(false), [pathname]);

  const textLink = `hidden h-10 items-center gap-2 whitespace-nowrap text-[0.875rem] font-medium tracking-[0.01em] transition-colors duration-200 ${
    light ? "text-white/80 hover:text-white" : "text-foreground/70 hover:text-foreground"
  }`;

  return (
    <header
      className={`no-print fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ease-premium ${
        light ? "bg-transparent" : "border-b border-border/70 bg-background/92 backdrop-blur-xl"
      }`}
      data-testid="site-header"
      data-state={searchVisible ? "search" : "nav"}
      data-light={light ? "true" : "false"}
      data-scrolled={scrolled ? "true" : "false"}
    >
      <div
        className={`mx-auto grid w-full max-w-[1480px] grid-cols-[auto_1fr] items-center gap-4 px-5 transition-[height] duration-300 ease-premium sm:px-8 lg:grid-cols-[1fr_auto_1fr] lg:gap-8 lg:px-10 ${
          scrolled ? "h-16 lg:h-[72px]" : "h-20 lg:h-24"
        }`}
      >
        <Link to="/" className="flex w-fit shrink-0 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="header-logo-link" aria-label="Rosendal Fjordhotel – heim">
          <Logo light={light} className={scrolled ? "h-7 lg:h-8" : "h-8 lg:h-9"} />
        </Link>

        <div className="relative hidden min-w-0 justify-self-center lg:block">
          <AnimatePresence initial={false} mode="wait">
            {searchVisible ? (
              <motion.div
                key="search"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                className="w-[560px] max-w-[46vw]"
                data-testid="sticky-booking-bar"
              >
                <BookingBar variant="compact" testid="sticky-booking-bar-inner" />
              </motion.div>
            ) : (
              <motion.div
                key="nav"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 3 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <DesktopNav light={light} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-end gap-5 lg:gap-6">
          <a href={PHONE.tel} className={`${textLink} tabular 2xl:inline-flex`} data-testid="header-phone-link">
            <Phone className="h-[15px] w-[15px]" strokeWidth={1.75} /> {PHONE.display}
          </a>
          <Link to="/mi-bestilling" className={`${textLink} link-grow md:inline-flex lg:hidden xl:inline-flex`} data-testid="header-my-booking-link">
            {tx("Mi bestilling", "My booking")}
          </Link>
          <div className="flex items-center gap-1.5">
            <LangMenu light={light} />
            <Button
              asChild
              className={`h-10 px-5 ${light ? "bg-white text-foreground hover:bg-white/90" : ""}`}
              data-testid="header-book-cta"
            >
              <Link to="/bestill">{tx("Bestill rom", "Book a room")}</Link>
            </Button>
            <button
              type="button"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-md transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden ${light ? "text-white hover:bg-white/10" : "text-foreground hover:bg-foreground/[0.05]"}`}
              onClick={() => setOpen(true)}
              aria-label={tx("Opne meny", "Open menu")}
              data-testid="header-mobile-menu-button"
            >
              <Menu className="h-6 w-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu – full-height linen sheet, large quiet links */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col border-0 bg-background p-0 sm:max-w-md [&>button]:hidden" data-testid="mobile-menu">
          <div className="flex h-20 items-center justify-between px-5">
            <Logo />
            <button type="button" onClick={() => setOpen(false)} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-foreground/[0.05]" aria-label={tx("Lukk", "Close")} data-testid="mobile-menu-close"><X className="h-6 w-6" strokeWidth={1.5} /></button>
          </div>
          <SheetTitle className="sr-only">{tx("Meny", "Menu")}</SheetTitle>
          <SheetDescription className="sr-only">{tx("Navigasjon", "Navigation")}</SheetDescription>
          <nav className="flex-1 overflow-y-auto px-5 pt-4">
            <ul>
              {[{ to: "/", nn: "Heim", en: "Home", id: "home" }, ...NAV].map((n) => (
                <li key={n.to} className="border-b border-border">
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-${n.id}`}
                    className={({ isActive }) => `group flex items-center justify-between py-4 text-[1.75rem] font-medium leading-none tracking-[-0.015em] transition-colors ${isActive ? "text-foreground" : "text-foreground/65 hover:text-foreground"}`}
                  >
                    {tx(n.nn, n.en)}
                    <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" strokeWidth={1.5} />
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="mt-8 grid grid-cols-2 gap-3">
              <Link to="/mi-bestilling" className="flex flex-col gap-4 rounded-lg border border-border p-4 transition-colors hover:bg-secondary" data-testid="mobile-nav-my-booking">
                <CalendarCheck className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                <span className="text-sm font-medium text-foreground">{tx("Mi bestilling", "My booking")}</span>
              </Link>
              <a href={PHONE.tel} className="flex flex-col gap-4 rounded-lg border border-border p-4 transition-colors hover:bg-secondary" data-testid="mobile-menu-phone">
                <Phone className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                <span className="text-sm font-medium text-foreground tabular">{PHONE.display}</span>
              </a>
            </div>
            <div className="mt-8 flex items-center justify-between gap-4">
              <p className="label-caps text-muted-foreground">{tx("Språk", "Language")}</p>
              <LangSwitch testid="mobile-language-toggle" />
            </div>
          </nav>
          <div className="space-y-3 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <Button asChild size="xl" className="w-full" data-testid="mobile-menu-book-cta"><Link to="/bestill">{tx("Bestill rom", "Book a room")}</Link></Button>
            <p className="text-center text-xs text-muted-foreground">Rosendalsvegen 46 · 5470 Rosendal</p>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};
